import React from "react";
import { TopBar } from "@/components/TopBar";
import {
  apiListOrders,
  apiListCustomers,
  apiCreateOrder,
  apiUpdateOrder,
  apiChangeOrderStatus,
  apiGetOrder,
  apiListSpks,
  apiCreateSpkFromOrder,
  apiListNotas,
  apiCreateNotaFromSpk,
  formatApiErrorDetail,
} from "@/lib/api";
import { Loader2, Plus, Trash2, X, RefreshCw, CheckCircle2, Ban, Eye, ClipboardList, Receipt } from "lucide-react";
import { qtyTeks } from "@/lib/format";

const KATEGORI = ["BANNER", "DTF", "JERSEY", "CUSTOM"];
const rupiah = (n) => "Rp" + Number(n || 0).toLocaleString("id-ID");
const newItem = () => ({ kategori_produk: "BANNER", produk: "", jenis_pekerjaan: "", detail_pekerjaan: "", quantity: 1, satuan: "pcs" });
const STATUS_STYLE = {
  DRAFT: "border-amber-500/30 bg-amber-500/10 text-amber-300",
  CONFIRMED: "border-emerald-500/30 bg-emerald-500/10 text-emerald-300",
  CANCELLED: "border-red-500/30 bg-red-500/10 text-red-300",
};

export default function OrderManagement() {
  const [orders, setOrders] = React.useState([]);
  const [customers, setCustomers] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState(null);
  const [notice, setNotice] = React.useState(null);
  const [showForm, setShowForm] = React.useState(false);
  const [editingId, setEditingId] = React.useState(null);
  const [saving, setSaving] = React.useState(false);
  const [detail, setDetail] = React.useState(null);
  const [spkByOrder, setSpkByOrder] = React.useState({});
  const [notaByOrder, setNotaByOrder] = React.useState({});
  const [form, setForm] = React.useState({ customer_id: "", items: [newItem()], diskon: 0, catatan: "" });

  const load = React.useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [o, c, s, n] = await Promise.all([apiListOrders(), apiListCustomers(), apiListSpks(), apiListNotas()]);
      setOrders(o);
      setCustomers(c);
      const smap = {};
      s.forEach((spk) => { smap[spk.order_id] = spk; });
      setSpkByOrder(smap);
      const nmap = {};
      n.forEach((nt) => { nmap[nt.order_id] = nt; });
      setNotaByOrder(nmap);
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  React.useEffect(() => { load(); }, [load]);

  const custName = (id) => customers.find((c) => c.id === id)?.nama || "—";
  const flash = (m) => { setNotice(m); setTimeout(() => setNotice(null), 3000); };

  const createSpk = async (order) => {
    setError(null);
    try {
      const spk = await apiCreateSpkFromOrder(order.id);
      flash(`SPK ${spk.nomor_spk} dibuat dari ${order.nomor_order}`);
      await load();
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  const createNota = async (order) => {
    setError(null);
    const spk = spkByOrder[order.id];
    if (!spk) { setError("Order ini belum memiliki SPK."); return; }
    try {
      const nota = await apiCreateNotaFromSpk(spk.id);
      flash(`Nota ${nota.nomor_nota} dibuat dari ${spk.nomor_spk}`);
      await load();
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };
  const resetForm = () => { setForm({ customer_id: "", items: [newItem()], diskon: 0, catatan: "" }); setEditingId(null); setShowForm(false); };

  const addItem = () => setForm({ ...form, items: [...form.items, newItem()] });
  const removeItem = (i) => setForm({ ...form, items: form.items.filter((_, idx) => idx !== i) });
  const updateItem = (i, patch) => setForm({ ...form, items: form.items.map((it, idx) => (idx === i ? { ...it, ...patch } : it)) });

  const onSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setSaving(true);
    const payload = {
      customer_id: form.customer_id,
      diskon: parseInt(form.diskon, 10) || 0,
      catatan: form.catatan || null,
      items: form.items.map((it) => ({
        kategori_produk: it.kategori_produk,
        produk: it.produk || null,
        jenis_pekerjaan: it.jenis_pekerjaan,
        detail_pekerjaan: it.detail_pekerjaan || null,
        quantity: parseInt(it.quantity, 10) || 0,
        satuan: it.satuan || "pcs",
      })),
    };
    try {
      let result;
      if (editingId) { result = await apiUpdateOrder(editingId, payload); flash(`Order diperbarui — total ${rupiah(result.total)}`); }
      else { result = await apiCreateOrder(payload); flash(`Order ${result.nomor_order} dibuat — total ${rupiah(result.total)}`); }
      resetForm();
      await load();
      setDetail(result);
    } catch (e2) {
      setError(formatApiErrorDetail(e2.response?.data?.detail) || e2.message);
    } finally {
      setSaving(false);
    }
  };

  const startEdit = async (o) => {
    const full = await apiGetOrder(o.id);
    setEditingId(full.id);
    setForm({
      customer_id: full.customer_id,
      diskon: full.diskon,
      catatan: full.catatan || "",
      items: full.items.map((it) => ({ kategori_produk: it.kategori_produk, produk: it.produk || "", jenis_pekerjaan: it.jenis_pekerjaan, detail_pekerjaan: it.detail_pekerjaan || "", quantity: it.quantity, satuan: it.satuan })),
    });
    setShowForm(true);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const changeStatus = async (o, status) => {
    setError(null);
    try {
      const updated = await apiChangeOrderStatus(o.id, status);
      flash(`Status order ${o.nomor_order} → ${status}`);
      await load();
      if (detail?.id === o.id) setDetail(updated);
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  const inputCls = "mt-1 w-full rounded-lg border border-slate-700 bg-slate-950 px-3 py-2 text-sm text-slate-100 outline-none focus:border-amber-500/60";
  const labelCls = "text-[13px] font-semibold text-slate-200";

  return (
    <div data-testid="order-management-page" className="min-h-screen bg-slate-950 text-slate-200">
      <TopBar />
      <main className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-slate-100">Manajemen Order</h1>
            <p className="text-sm text-slate-400 mt-1">Harga & total dihitung backend dari master tarif (snapshot).</p>
          </div>
          <button data-testid="order-new-button" onClick={() => { resetForm(); setShowForm(true); }} className="inline-flex items-center gap-2 rounded-lg bg-amber-500 px-4 py-2 text-sm font-semibold text-slate-950 hover:bg-amber-400"><Plus size={16} /> Order Baru</button>
        </div>

        {notice && <div data-testid="order-notice" className="rounded-lg border border-emerald-500/30 bg-emerald-500/10 px-4 py-2.5 text-sm text-emerald-300">{notice}</div>}
        {error && <div data-testid="order-error" className="rounded-lg border border-red-500/30 bg-red-500/10 px-4 py-2.5 text-sm text-red-300">{error}</div>}

        {showForm && (
          <form data-testid="order-form" onSubmit={onSubmit} className="rounded-xl border border-slate-800 bg-slate-900/60 p-6 space-y-5">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-semibold text-slate-200">{editingId ? "Edit Order (DRAFT)" : "Order Baru"}</h2>
              <button type="button" data-testid="order-form-close" onClick={resetForm} className="text-slate-400 hover:text-slate-200"><X size={18} /></button>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className={labelCls}>Customer</label>
                <select data-testid="order-customer" value={form.customer_id} onChange={(e) => setForm({ ...form, customer_id: e.target.value })} required className={inputCls}>
                  <option value="">— pilih customer —</option>
                  {customers.map((c) => (<option key={c.id} value={c.id}>{c.nama} · {c.nomor_telepon}</option>))}
                </select>
              </div>
              <div>
                <label className={labelCls}>Catatan (opsional)</label>
                <input data-testid="order-catatan" value={form.catatan} onChange={(e) => setForm({ ...form, catatan: e.target.value })} className={inputCls} />
              </div>
            </div>

            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <span className={labelCls}>Item</span>
                <button type="button" data-testid="order-add-item" onClick={addItem} className="inline-flex items-center gap-1.5 rounded-md border border-slate-700 bg-slate-800/60 px-3 py-1.5 text-xs text-slate-300 hover:border-amber-500/40 hover:text-amber-300"><Plus size={13} /> Tambah Item</button>
              </div>
              {form.items.map((it, i) => (
                <div key={i} data-testid={`order-item-${i}`} className="grid grid-cols-1 sm:grid-cols-12 gap-3 items-end rounded-lg border border-slate-800 bg-slate-950/40 p-3">
                  <div className="sm:col-span-2">
                    <label className={labelCls}>Kategori</label>
                    <select data-testid={`order-item-kategori-${i}`} value={it.kategori_produk} onChange={(e) => updateItem(i, { kategori_produk: e.target.value })} className={inputCls}>
                      {KATEGORI.map((k) => (<option key={k} value={k}>{k}</option>))}
                    </select>
                  </div>
                  <div className="sm:col-span-2">
                    <label className={labelCls}>Produk</label>
                    <input data-testid={`order-item-produk-${i}`} value={it.produk} onChange={(e) => updateItem(i, { produk: e.target.value })} className={inputCls} />
                  </div>
                  <div className="sm:col-span-2">
                    <label className={labelCls}>Jenis Pekerjaan</label>
                    <input data-testid={`order-item-jenis-${i}`} value={it.jenis_pekerjaan} onChange={(e) => updateItem(i, { jenis_pekerjaan: e.target.value })} required className={inputCls} />
                  </div>
                  <div className="sm:col-span-2">
                    <label className={labelCls}>Detail</label>
                    <input data-testid={`order-item-detail-${i}`} value={it.detail_pekerjaan} onChange={(e) => updateItem(i, { detail_pekerjaan: e.target.value })} className={inputCls} />
                  </div>
                  <div className="sm:col-span-1">
                    <label className={labelCls}>Qty</label>
                    <input data-testid={`order-item-qty-${i}`} type="number" min="1" value={it.quantity} onChange={(e) => updateItem(i, { quantity: e.target.value })} required className={inputCls} />
                  </div>
                  <div className="sm:col-span-2">
                    <label className={labelCls}>Satuan</label>
                    <input data-testid={`order-item-satuan-${i}`} value={it.satuan} onChange={(e) => updateItem(i, { satuan: e.target.value })} className={inputCls} />
                  </div>
                  <div className="sm:col-span-1 flex justify-end">
                    {form.items.length > 1 && <button type="button" data-testid={`order-item-remove-${i}`} onClick={() => removeItem(i)} className="rounded-md border border-slate-700 bg-slate-800/60 p-2 text-slate-400 hover:border-red-500/40 hover:text-red-300"><Trash2 size={14} /></button>}
                  </div>
                </div>
              ))}
            </div>

            <div className="flex flex-wrap items-end gap-4">
              <div>
                <label className={labelCls}>Diskon (Rp)</label>
                <input data-testid="order-diskon" type="number" min="0" value={form.diskon} onChange={(e) => setForm({ ...form, diskon: e.target.value })} className={inputCls} />
              </div>
              <p className="text-xs text-slate-500 flex-1">Harga per item diambil otomatis dari master tarif aktif. Jika tarif tidak ditemukan, backend akan menolak dengan pesan jelas.</p>
              <button data-testid="order-submit" type="submit" disabled={saving} className="inline-flex items-center gap-2 rounded-lg bg-amber-500 px-5 py-2 text-sm font-semibold text-slate-950 hover:bg-amber-400 disabled:opacity-60">
                {saving ? <Loader2 className="animate-spin" size={16} /> : <Plus size={16} />}
                {editingId ? "Simpan Order" : "Buat Order"}
              </button>
            </div>
          </form>
        )}

        {detail && (
          <div data-testid="order-detail" className="rounded-xl border border-amber-500/30 bg-slate-900/60 p-6 space-y-3">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-semibold text-slate-100">Detail {detail.nomor_order}</h2>
              <button data-testid="order-detail-close" onClick={() => setDetail(null)} className="text-slate-400 hover:text-slate-200"><X size={18} /></button>
            </div>
            <div className="text-sm text-slate-400">Customer: <span className="text-slate-200">{custName(detail.customer_id)}</span> · Dibuat oleh: <span className="text-slate-200">{detail.dibuat_oleh_username}</span> · Status: <span className={`text-xs font-mono px-2 py-0.5 rounded border ${STATUS_STYLE[detail.status_order]}`}>{detail.status_order}</span></div>
            <table className="w-full text-sm">
              <thead><tr className="text-left text-xs font-semibold text-slate-500 bg-slate-900 border-b border-slate-800"><th className="py-2">Item</th><th className="py-2 text-right">Qty</th><th className="py-2 text-right">Harga</th><th className="py-2 text-right">Subtotal</th></tr></thead>
              <tbody>
                {detail.items.map((it, i) => (
                  <tr key={i} className="border-b border-slate-800/60">
                    <td className="py-2 text-slate-300">{it.kategori_produk}/{it.jenis_pekerjaan}{it.produk ? ` · ${it.produk}` : ""}</td>
                    <td className="py-2 text-right font-mono">{qtyTeks(it)}</td>
                    <td className="py-2 text-right font-mono">{rupiah(it.harga)}</td>
                    <td className="py-2 text-right font-mono text-slate-200">{rupiah(it.subtotal)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div className="flex justify-end gap-6 text-sm font-mono">
              <span className="text-slate-400">Subtotal: <span className="text-slate-200">{rupiah(detail.subtotal)}</span></span>
              <span className="text-slate-400">Diskon: <span className="text-slate-200">{rupiah(detail.diskon)}</span></span>
              <span className="text-amber-300 font-semibold" data-testid="order-detail-total">Total: {rupiah(detail.total)}</span>
            </div>
            <div className="pt-2 border-t border-slate-800 space-y-2">
              <div className="flex justify-end">
                {spkByOrder[detail.id] ? (
                  <span data-testid="order-detail-spk" className="text-sm text-slate-400">SPK dibuat: <span className="font-mono text-amber-300">{spkByOrder[detail.id].nomor_spk}</span></span>
                ) : detail.status_order === "CONFIRMED" ? (
                  <button data-testid="order-detail-create-spk" onClick={() => createSpk(detail)} className="inline-flex items-center gap-2 rounded-lg bg-emerald-500 px-5 py-2 text-sm font-semibold text-slate-950 hover:bg-emerald-400">
                    <ClipboardList size={16} /> Buat SPK
                  </button>
                ) : (
                  <span className="text-xs text-slate-500">SPK hanya dapat dibuat dari order CONFIRMED.</span>
                )}
              </div>
              {spkByOrder[detail.id] && (
                <div className="flex justify-end" data-testid="order-detail-nota-row">
                  {notaByOrder[detail.id] ? (
                    <span data-testid="order-detail-nota" className="text-sm text-slate-400">Nota dibuat: <span className="font-mono text-emerald-300">{notaByOrder[detail.id].nomor_nota}</span></span>
                  ) : (
                    <button data-testid="order-detail-create-nota" onClick={() => createNota(detail)} className="inline-flex items-center gap-2 rounded-lg bg-cyan-500 px-5 py-2 text-sm font-semibold text-slate-950 hover:bg-cyan-400">
                      <Receipt size={16} /> Buat Nota
                    </button>
                  )}
                </div>
              )}
            </div>
          </div>
        )}

        <div className="rounded-xl border border-slate-800 bg-slate-900/60 overflow-hidden">
          <div className="flex items-center justify-between px-5 py-3 border-b border-slate-800">
            <h2 className="text-[15px] font-bold text-soq-biru-tua">Daftar Order</h2>
            <button data-testid="order-refresh" onClick={load} className="inline-flex items-center gap-2 text-xs font-mono text-slate-400 hover:text-slate-200"><RefreshCw size={14} className={loading ? "animate-spin" : ""} /> Refresh</button>
          </div>
          {loading ? (
            <div className="p-8 flex items-center justify-center text-slate-400"><Loader2 className="animate-spin mr-2" size={18} /> Memuat…</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="order-table">
                <thead>
                  <tr className="text-left text-xs font-semibold text-slate-500 bg-slate-900 border-b border-slate-800">
                    <th className="px-4 py-3">No. Order</th>
                    <th className="px-4 py-3">Customer</th>
                    <th className="px-4 py-3 text-right">Total</th>
                    <th className="px-4 py-3">Status</th>
                    <th className="px-4 py-3 text-right">Aksi</th>
                  </tr>
                </thead>
                <tbody>
                  {orders.map((o) => (
                    <tr key={o.id} data-testid={`order-row-${o.nomor_order}`} className="border-b border-slate-800/60 hover:bg-slate-800/30">
                      <td className="px-4 py-3 font-mono text-amber-300">{o.nomor_order}</td>
                      <td className="px-4 py-3 text-slate-300">{custName(o.customer_id)}</td>
                      <td className="px-4 py-3 text-right font-mono text-slate-200">{rupiah(o.total)}</td>
                      <td className="px-4 py-3"><span data-testid={`order-status-${o.nomor_order}`} className={`text-xs font-mono px-2 py-1 rounded-md border ${STATUS_STYLE[o.status_order]}`}>{o.status_order}</span></td>
                      <td className="px-4 py-3">
                        <div className="flex items-center justify-end gap-2">
                          <button data-testid={`order-view-${o.nomor_order}`} onClick={async () => setDetail(await apiGetOrder(o.id))} className="inline-flex items-center rounded-md border border-slate-700 bg-slate-800/60 px-2.5 py-1.5 text-xs text-slate-300 hover:border-cyan-500/40 hover:text-cyan-300"><Eye size={13} /></button>
                          {o.status_order === "DRAFT" && <button data-testid={`order-edit-${o.nomor_order}`} onClick={() => startEdit(o)} className="rounded-md border border-slate-700 bg-slate-800/60 px-2.5 py-1.5 text-xs text-slate-300 hover:border-amber-500/40 hover:text-amber-300">Edit</button>}
                          {o.status_order === "DRAFT" && <button data-testid={`order-confirm-${o.nomor_order}`} onClick={() => changeStatus(o, "CONFIRMED")} className="inline-flex items-center gap-1 rounded-md border border-emerald-500/30 bg-emerald-500/10 px-2.5 py-1.5 text-xs text-emerald-300 hover:bg-emerald-500/20"><CheckCircle2 size={13} /> Confirm</button>}
                          {o.status_order !== "CANCELLED" && <button data-testid={`order-cancel-${o.nomor_order}`} onClick={() => changeStatus(o, "CANCELLED")} className="inline-flex items-center gap-1 rounded-md border border-red-500/30 bg-red-500/10 px-2.5 py-1.5 text-xs text-red-300 hover:bg-red-500/20"><Ban size={13} /> Cancel</button>}
                        </div>
                      </td>
                    </tr>
                  ))}
                  {orders.length === 0 && <tr><td colSpan={5} className="px-4 py-8 text-center text-slate-500">Belum ada order.</td></tr>}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
